/**
 * Row data: the shared vocabularies of 1.0b deliverable 1. Each list is a baseline section 3
 * `CHECK` list, value for value and in its order, with the amendments named beside it
 * (amendments 2.1 section 4; amendments 2.2 section 7). `src/domain/enums.ts` builds the Zod
 * schemas from these lists; nothing else defines them. Pure data (excluded from mutation testing).
 */
// ---- Documents and types ----
/** `document.library`, `doc_type.library` (A2.1 §4). */
export const LIBRARIES = ['canon', 'reference'];
/** `document.canon_status`, with `reference` (A2.1 §4). */
export const CANON_STATUSES = ['draft', 'canon', 'retired', 'reference'];
/** `document.ai_context`, with `n_a` (A2.1 §4). */
export const AI_CONTEXTS = ['always', 'when_linked', 'when_mentioned', 'never', 'n_a'];
/** `doc_type.capabilities`, with `has_card` (A2.1 §4). */
export const CAPABILITIES = [
    'speaker',
    'place',
    'holder',
    'has_sheet',
    'structural',
    'playable',
    'discoverable',
    'session',
    'has_card',
];
/** The capabilities a user type may hold: every one but `session` and `has_card`. */
export const ASSIGNABLE_CAPABILITIES = [
    'speaker',
    'place',
    'holder',
    'has_sheet',
    'structural',
    'playable',
    'discoverable',
];
/** `field_def.kind` (thirteen). */
export const FIELD_KINDS = [
    'text',
    'long_text',
    'number',
    'boolean',
    'select',
    'multi_select',
    'url',
    'real_date',
    'world_date',
    'doc_ref',
    'doc_refs',
    'asset',
    'dice',
];
/** `field_value.source`, with `rules` (A2.1 §4). */
export const FIELD_VALUE_SOURCES = ['prop', 'link', 'placement', 'state', 'rules'];
/** `doc_type.foundry_kind`. */
export const FOUNDRY_KINDS = ['JournalEntry', 'Actor', 'Item'];
/** `template.variant`. */
export const TEMPLATE_VARIANTS = ['blank', 'quick', 'full', 'custom'];
/** `document.play_state`. */
export const PLAY_STATES = ['n_a', 'unplayed', 'played', 'skipped', 'diverged'];
/** `play_record.state`. */
export const PLAY_RECORD_STATES = ['unplayed', 'played', 'skipped', 'diverged'];
/** `document.discovery`, `secret.state`, `knowledge_event.state`. */
export const DISCOVERY_STATES = ['unknown', 'partial', 'known'];
/** `document_revision.reason`. */
export const REVISION_REASONS = [
    'checkpoint',
    'manual',
    'ai_accept',
    'import',
    'restore',
    'merge',
    'retype',
    'ingest',
    'conflict',
];
/** `import_map.source`. */
export const IMPORT_SOURCES = ['notion'];
/** `import_map.via`, with `relation` (amendments 2.2 §7). */
export const IMPORT_VIAS = ['import', 'paste', 'relation'];
/** `import_map.resolution`. */
export const IMPORT_RESOLUTIONS = [
    'created',
    'merged_into_existing',
    'kept_both',
    'skipped',
];
/** `change_log.op`. */
export const CHANGE_LOG_OPS = ['insert', 'update', 'delete'];
/** `change_log.origin`: who caused a change. */
export const CHANGE_ORIGINS = [
    'manual',
    'run_mode',
    'review',
    'ai',
    'import',
    'system',
    'undo',
    // CS r5a: a bootstrap's ledger rows log their own origin (1.M-1's appends log the record's
    // origin), so the list holds every RECORD_ORIGINS value (DECISIONS 217 ruling 35).
    'bootstrap',
];
// ---- Assets, mentions, workspace ----
/** `asset.kind`. */
export const ASSET_KINDS = ['image', 'source', 'import', 'raw', 'export'];
/** `asset_variant.variant`. */
export const ASSET_VARIANTS = ['thumb', 'w1200'];
/** `asset_ref.via`. */
export const ASSET_REF_VIAS = ['body', 'cover', 'prop', 'source', 'sheet'];
/** `mention.kind`, with `rulecard` (A2.1 §1 and §4). */
export const MENTION_KINDS = [
    'mention',
    'speaker',
    'topic_speaker',
    'location',
    'statblock',
    'embed',
    'date',
    'rulecard',
];
/** `sidebar_item.kind`. */
export const SIDEBAR_ITEM_KINDS = ['document', 'view', 'group'];
// ---- Links ----
/** `link.origin`. */
export const LINK_ORIGINS = ['manual', 'field', 'ai', 'import', 'ingest'];
/** `link.span_status`. */
export const SPAN_STATUSES = ['ok', 'invalid'];
/** `inbox_dismissal.scope`. */
export const INBOX_DISMISSAL_SCOPES = ['once', 'permanent'];
// ---- Chronology ----
/**
 * `link.valid_from_edge` and `valid_to_edge`, `entity_state.from_edge` and `to_edge`,
 * `chron_constraint.before_edge` and `after_edge`.
 */
export const CHRON_EDGES = ['start', 'end'];
/** `time_point.owner_kind`. */
export const TIME_POINT_OWNER_KINDS = [
    'placement_start',
    'placement_end',
    'link_from',
    'link_to',
    'state_from',
    'state_to',
    'clock_origin',
    'clock_set',
    'capture',
    'knowledge',
];
/** `time_point.precision`, coarsest first. */
export const PRECISIONS = [
    'era',
    'century',
    'decade',
    'year',
    'season',
    'month',
    'week',
    'day',
    'hour',
    'minute',
];
/** `time_point.qualifier`. */
export const QUALIFIERS = ['exact', 'approximate', 'uncertain'];
/** `time_point.tick_status`. */
export const TICK_STATUSES = ['ok', 'invalid', 'open_lo', 'open_hi'];
/** The `part` key of `time_point.lo_parts` and `hi_parts`: the first, middle or last third. */
export const PARTS_OF_UNIT = ['early', 'mid', 'late'];
/**
 * `time_point.review_reason` (a column without a `CHECK`): the eleven codes of 1.K-1's
 * deliverables 5 and 15, in their order. The calendar engine imports this list and defines none.
 */
export const TICK_REVIEW_REASONS = [
    'calendar_invalid',
    'unknown_key',
    'incomplete_parts',
    'day_out_of_range',
    'time_out_of_range',
    'week_out_of_range',
    'not_in_year',
    'outside_era',
    'inverted_range',
    'out_of_tick_range',
    'key_removed',
];
/** `placement.kind`. */
export const PLACEMENT_KINDS = ['moment', 'span', 'undated'];
/** `placement.status`. */
export const PLACEMENT_STATUSES = ['canon', 'planned', 'rumor', 'alternate'];
/** `placement.effective_status`. */
export const EFFECTIVE_STATUSES = ['canon', 'planned', 'rumor', 'alternate', 'retired'];
/** `placement.origin`. */
export const PLACEMENT_ORIGINS = [
    'manual',
    'field',
    'ai',
    'import',
    'ingest',
    'session',
    'auto',
];
/** `chron_constraint.relation`. */
export const CHRON_RELATIONS = ['before', 'same_time', 'during'];
/** `chron_constraint.status`. */
export const CONSTRAINT_STATUSES = [
    'active',
    'dropped_cycle',
    'contradiction',
    'lag_unresolved',
];
/** `chron_violation.kind`. */
export const VIOLATION_KINDS = [
    'cycle',
    'date_contradiction',
    'invalid_date',
    'lag_unsatisfiable',
    'lag_unresolved',
];
/** `chron_constraint.lag_unit`, `clock_advance.delta_unit`. */
export const TIME_UNITS = ['minute', 'hour', 'day', 'week', 'month', 'year'];
/** `clock_advance.kind`. */
export const CLOCK_ADVANCE_KINDS = ['delta', 'set'];
/**
 * `clock_advance.reason`. `session_capture` (CS r6c addendum item 4; 2.F's "Session capture"): an
 * advance promoted from a time capture after the session, appended last (migration 0020 widens the
 * column's check).
 */
export const CLOCK_REASONS = [
    'scene',
    'short_rest',
    'long_rest',
    'travel',
    'downtime',
    'manual',
    'correction',
    'bootstrap',
    'session_capture',
];
// ---- Play, discovery, sessions ----
/** `knowledge_event.action`. */
export const KNOWLEDGE_ACTIONS = ['assert', 'retract'];
/**
 * `knowledge_event.origin` and `play_record.origin`. CS r5a (DECISIONS 212 item 3, 217 ruling 35)
 * adds `undo` (2.L's compensating `retract` rows) and `bootstrap` (2.J's past sessions), widening
 * both CHECKs in one migration.
 */
export const RECORD_ORIGINS = [
    'manual',
    'run_mode',
    'review',
    'ai',
    'import',
    'undo',
    'bootstrap',
];
/**
 * The origins a request may send on `play.create`, `knowledge.create` and `clock.advance` (CS r5a
 * deliverable 142; open point 35, DECISIONS 217 ruling 27): the server writes every other one.
 */
export const CLIENT_RECORD_ORIGINS = ['manual', 'run_mode'];
/** `session_meta.status`. */
export const SESSION_STATUSES = ['planned', 'prepped', 'running', 'played', 'processed'];
/** `session_meta.origin`. */
export const SESSION_ORIGINS = ['live', 'bootstrap'];
/** `capture.kind` (1.0a stage 2 creates `capture`: orchestrator ruling 7). */
export const CAPTURE_KINDS = [
    'note',
    'stub',
    'time',
    'loot',
    'reveal',
    'state',
    'decision',
];
/** `capture.status`. */
export const CAPTURE_STATUSES = ['open', 'promoted', 'dismissed'];
/** `session_item.kind` (baseline section 3, the run sheet; CS r5a deliverable 142). */
export const SESSION_ITEM_KINDS = ['scene', 'document', 'secret', 'note'];
/**
 * `session_source.kind` (A2.1 section 3: vendor-neutral, the GM chooses it; nothing is parsed or
 * sniffed in Phase 2). The file's asset has `ASSET_KINDS`' existing `source` (DECISIONS 217
 * ruling 24).
 */
export const SESSION_SOURCE_KINDS = ['gma_summary', 'transcript', 'foundry_chat', 'other'];
/** `session_source.status`, baseline section 3's six; an upload starts `uploaded`. */
export const SESSION_SOURCE_STATUSES = [
    'uploaded',
    'parsed',
    'mapped',
    'failed',
    'superseded',
    'purged',
];
/** The parts of a session a `session.changed` event names (CS r5a deliverable 146). */
export const SESSION_CHANGE_PARTS = ['meta', 'items', 'attendance', 'captures', 'sources'];
// ---- 5e, reference library, Foundry ----
/** `actor_sheet.kind`. */
export const SHEET_KINDS = ['statblock', 'character'];
/**
 * `actor_sheet.rules_version` (baseline section 3: 2014 rules only; CS r1c, DECISIONS 93 item 3).
 * `RulesVersion` of `@codex/dnd5e/schema` also names `2024`, which a sheet never stores.
 */
export const SHEET_RULES_VERSIONS = ['2014'];
/** `actor_sheet.derived_source` (amendments 2.2 §7). */
export const DERIVED_SOURCES = ['form', 'recomputed', 'macro'];
/** `rules_record.kind` (A2.1 §4). */
export const RULES_RECORD_KINDS = ['spell', 'item'];
/** `reference_entry.kind` (A2.1 §4). */
export const REFERENCE_KINDS = ['creature', 'spell', 'item'];
/** `foundry_map.entity_kind`. */
export const FOUNDRY_ENTITY_KINDS = [
    'document',
    'actor_sheet',
    'sheet_feature',
    'secret',
    'journal_page',
];
/** `foundry_map.origin`. */
export const FOUNDRY_MAP_ORIGINS = ['minted', 'imported', 'matched'];
// ---- Built-in keys ----
/**
 * The keys of the fourteen built-in canon types (PLAN section 2, baseline section 3 "Seeds"), in
 * the seed order. Names, icons and capabilities are 1.0a's seed content, not contracts. `spell` is
 * appended by CS r6c (A2.1 §4 "Homebrew authoring", the homebrew spell card's type): last in this
 * list, so no seeded id or index moves, while its sort key places it between `session` and `note`
 * in the sidebar (the seed's `aBV`).
 */
export const BUILTIN_TYPE_KEYS = [
    'pc',
    'npc',
    'creature',
    'encounter',
    'location',
    'faction',
    'item',
    'lore',
    'arc',
    'quest',
    'script',
    'session',
    'note',
    'spell',
];
/** The keys of the three locked built-in reference types (A2.1 §4). */
export const REFERENCE_TYPE_KEYS = ['ref_creature', 'ref_spell', 'ref_item'];
/**
 * The icons the server accepts for a type (CS r2; DECISIONS 119 item 23): the UI kit's fifteen
 * `TYPE_KEYS`, each drawn with a glyph and a color token of its own: the fourteen built-in types in
 * sidebar order (the thirteen canon keys and `spell`), then the demo's `deity`. A list, not yet a
 * bound: `TypeCreateRequestV1.icon` and `TypeUpdateRequestV1.icon` still take any label, so a
 * stored icon outside it stays readable; checking a new one against it is the server's (1.B-1).
 */
export const TYPE_ICON_KEYS = [
    'pc',
    'npc',
    'creature',
    'encounter',
    'location',
    'faction',
    'item',
    'lore',
    'arc',
    'quest',
    'script',
    'session',
    'spell',
    'note',
    'deity',
];
/**
 * The fourteen built-in link-type keys: the thirteen baseline section 3 seeds, in their order,
 * and `variant_of` (A2.1 §4). Labels and link-type flags are 1.0a's seed content.
 */
export const LINK_TYPE_KEYS = [
    'contains',
    'depends_on',
    'given_by',
    'set_in',
    'appears_in',
    'reward_of',
    'uses_statblock',
    'member_of',
    'leads',
    'located_in',
    'held_by',
    'related_to',
    'includes_creature',
    'variant_of',
];
// ---- The Notion import (CS r4a, Phase 2a; 2a plan §2; DECISIONS 176 to 178) ----
/**
 * The three export kinds, amendments 2.2 §5's input modes in order, as 2.A deliverable 6 names
 * them: a workspace in HTML, a workspace in Markdown and CSV, and single pages in Markdown.
 */
export const IMPORT_EXPORT_KINDS = ['html', 'markdown_csv', 'markdown_pages'];
/**
 * 1.H's `PASTE_WARNING_CODES` (`packages/doc-model/src/paste/warnings.ts`), copied: contracts
 * never imports doc-model, so `import-plan.test.ts` holds the copy to the file.
 */
export const IMPORT_PASTE_WARNING_CODES = [
    'heading_clamped',
    'equation_as_code',
    'table_padded',
    'image_pending',
    'page_link_unresolved',
    'embed_as_link',
    'block_unwrapped',
    'block_dropped',
    'color_dropped',
    'link_dropped',
    'mention_as_text',
    'columns_flattened',
    'nesting_flattened',
    'empty_container_filled',
    'rebuilt_as_text',
    'repaired',
    'marks_dropped',
    'image_dropped',
    'slice_invalid',
    'markdown_hidden',
    'markdown_warning',
    'tier_downgraded',
    'hidden_as_gm_note',
    'html_unreadable',
];
/** 2.A deliverable 15's `EXPORT_WARNING_CODES`, copied (2.A has not merged; its spec holds both). */
export const IMPORT_EXPORT_WARNING_CODES = [
    'unknown_block',
    'export_file_link',
    'image_missing',
    'title_from_name',
    'property_block_unconfirmed',
    'unreadable_entry',
];
/**
 * The plan's own warnings: 2.A deliverables 20, 22 and 24 (`csv_row_unmatched` and
 * `csv_row_ambiguous` are 2.A deliverable 20's two CSV counts), 30 (`reference_target_ignored`),
 * and DECISIONS 178's apply-time skip `target_unavailable`.
 */
export const IMPORT_PLAN_WARNING_CODES = [
    'csv_ragged_row',
    'csv_row_unmatched',
    'csv_row_ambiguous',
    'relation_target_missing',
    'deep_tree',
    'tree_flattened',
    'reference_target_ignored',
    'target_unavailable',
];
/** Every warning a plan, a page or an apply records: a code, never text. */
export const IMPORT_WARNING_CODES = [
    ...IMPORT_PASTE_WARNING_CODES,
    ...IMPORT_EXPORT_WARNING_CODES,
    ...IMPORT_PLAN_WARNING_CODES,
];
/** 2.A deliverable 3's `ZIP_REFUSAL_CODES`, copied, each once. */
export const IMPORT_ZIP_REFUSAL_CODES = [
    'not_a_zip',
    'archive_error',
    'encrypted',
    'unsupported_method',
    'unsafe_path',
    'symlink',
    'duplicate_entry',
    'too_many_entries',
    'entry_too_large',
    'total_too_large',
    'ratio_exceeded',
    'size_mismatch',
    'too_many_parts',
    'nested_needs_scratch',
    'unknown_export_kind',
    'mixed_export_kind',
    'closed',
];
/** `import_run.failure_code`: an archive refusal, or a plan, apply or undo that failed. */
export const IMPORT_FAILURE_CODES = [
    ...IMPORT_ZIP_REFUSAL_CODES,
    'plan_failed',
    'apply_failed',
    'undo_failed',
];
/** `import_run.status`, in lifecycle order (2.B-1 deliverable 2 says which route moves which). */
export const IMPORT_RUN_STATUSES = [
    'uploading',
    'planning',
    'planned',
    'applying',
    'applied',
    'undoing',
    'undone',
    'failed',
    'cancelled',
];
/** `import_page.tier`: the kickoff plan's match tiers 1 to 4, first hit wins. */
export const IMPORT_MATCH_TIERS = ['mapped', 'stub', 'title', 'none'];
/** `import_page.choice`: what the GM (or a default) chose for a page. */
export const IMPORT_CHOICES = [
    'create',
    'fill',
    'pair',
    'pair_replace',
    'update',
    'keep_mine',
    'take_notion',
    'keep_both',
    'skip',
];
/**
 * The choices each tier offers (DECISIONS 176 items 4 and 5): `none` Create; `stub` Fill; `title`
 * Pair (keeps mine) or Pair and replace; `mapped` Update, Keep mine or Take Notion's; every tier
 * but `none` Keep both, and every tier Skip.
 */
export const IMPORT_CHOICES_BY_TIER = {
    mapped: ['update', 'keep_mine', 'take_notion', 'keep_both', 'skip'],
    stub: ['fill', 'keep_both', 'skip'],
    title: ['pair', 'pair_replace', 'keep_both', 'skip'],
    none: ['create', 'skip'],
};
/** Who set a page's choice: the proposal's default, the GM on the page, or a bulk choice. */
export const IMPORT_CHOICE_BY = ['default', 'gm', 'bulk'];
/** `import_page.reimport`: 2.A deliverable 32's seven re-import actions (DECISIONS 178). */
export const IMPORT_REIMPORT_ACTIONS = [
    'unchanged',
    'update',
    'changed_in_both',
    'fill_stub',
    'target_gone',
    'gone',
    'stub_unresolved',
];
/** `import_page.outcome`: what an apply or an undo did to a page. */
export const IMPORT_OUTCOMES = [
    'created',
    'filled',
    'paired',
    'kept_both',
    'skipped',
    'updated',
    'unchanged',
    'kept_mine',
    'undone',
    'undo_refused',
];
/** How a date property is read (`ImportMappingV1.properties[].dateAs`). */
export const IMPORT_DATE_AS = ['real_date', 'world_date', 'text'];
/** The GM's answer for single pages' property blocks (amendments 2.2 §5 "Property grammar" 3). */
export const IMPORT_PROPERTY_BLOCK_CHOICES = ['accept', 'keep_as_text'];
/** The bulk default for a `title` match (DECISIONS 176 item 4). */
export const IMPORT_PAIR_DEFAULTS = ['pair', 'pair_replace'];
/** The bulk default for a page changed in both places (DECISIONS 176 item 5). */
export const IMPORT_CHANGED_IN_BOTH_DEFAULTS = ['keep_mine', 'take_notion', 'keep_both'];
/** What the plan proposes for a Notion property: a field kind, a relation, a status, or nothing. */
export const IMPORT_PROPERTY_KINDS = [...FIELD_KINDS, 'relation', 'status', 'ignored'];
/** The phase an `import.progress` event reports. */
export const IMPORT_PROGRESS_PHASES = ['plan', 'apply', 'undo'];
// ---- Saved views (CS r4b deliverable 121) ----
/** `saved_view.layout`: baseline section 3's seven layouts, in its order. */
export const SAVED_VIEW_LAYOUTS = [
    'table',
    'gallery',
    'list',
    'board',
    'chronicle',
    'timeline',
    'calendar',
];
/** The layouts a `views.create` or `views.update` request takes in Phase 2a (2.K: table and list). */
export const SAVED_VIEW_REQUEST_LAYOUTS = ['table', 'list'];
// ---- The Foundry import (CS r6a deliverable 184; DECISIONS 213, 216, 220, 222) ----
/** `actor_import.status`: a preview, then applied, rejected, undone; a refused file keeps its row. */
export const ACTOR_IMPORT_STATUSES = [
    'previewed',
    'applied',
    'rejected',
    'undone',
    'refused',
];
/**
 * `actor_import.identity_source`: the identity chain's steps in their order (2.G deliverable 16,
 * spelled as amendments 2.2 §7's `identity_source` values), `ddb_id` fourth (DECISIONS 213 ruling
 * 9). Only a match at one of the first three can be silent.
 */
export const IDENTITY_SOURCES = [
    'macro',
    'export_source',
    'filename',
    'ddb_id',
    'fingerprint',
    'name_confirmed',
];
/** What the identity chain found: a silent match, proposals for the GM, or a new actor. */
export const IDENTITY_OUTCOMES = ['matched', 'proposed', 'new'];
/**
 * Why a match is proposed rather than silent (2.G deliverable 16's four), with 2.G's
 * `world_unknown` in its resolver's place (the campaign has a world, the file names none;
 * DECISIONS 787.2, added by hotfix/demo-p2c, 811 ruling 2).
 */
export const IDENTITY_REASONS = [
    'identity_conflict',
    'steps_disagree',
    'world_mismatch',
    'world_unknown',
    'compendium_uuid',
];
/**
 * The layouts `detectFoundryLayout` reports (`FoundryLayout` of `@codex/dnd5e/foundry`), the two a
 * mapper reads, the reserved `6.x` and `unsupported`. Not `FOUNDRY_LAYOUTS` of the dnd5e schema,
 * which lists only the two a stored sheet may name.
 */
export const FOUNDRY_DETECTED_LAYOUTS = ['5.0-5.2', '5.3', '6.x', 'unsupported'];
/** The Foundry actor types an import reads: a player character, or an NPC (a stat block). */
export const FOUNDRY_ACTOR_TYPES = ['character', 'npc'];
/** Why a file or an entry is `refused` (`actor_import.refusal`). */
export const FOUNDRY_REFUSAL_CODES = [
    'not_json',
    'json_limits',
    'not_an_actor',
    'unsupported_system',
    'reserved_layout',
    'unsupported_layout',
    'unsupported_actor_type',
    'schema_invalid',
];
/** A refresh plan's operations, one per origin path at most (2.G deliverable 17). */
export const REFRESH_OPS = [
    'update',
    'keep_local',
    'changed_in_both',
    'add',
    'remove',
    'removed_in_foundry',
];
/** The choices a refresh operation offers (2.G deliverable 17). */
export const REFRESH_CHOICES = ['take_foundry', 'keep_mine', 'skip', 'remove', 'keep'];
/** What an apply writes into: a new actor, a canon document with `has_sheet`, or a sheet. */
export const ACTOR_IMPORT_TARGETS = ['new', 'document', 'sheet'];
/**
 * The import's own warning codes (2.I s1), beside 2.G's `MAPPING_WARNING_CODES`: a file older
 * than the import last applied to its target, the same file already applied, a world other than
 * the setting's, and a biography cut at its bound. Named apart from the Notion importer's
 * `IMPORT_WARNING_CODES` (DECISIONS 766 open point 86); web and server share it from here, and
 * 2.I asserts its codes against it (DECISIONS 220 ruling 50).
 */
export const ACTOR_IMPORT_WARNING_CODES = [
    'older_than_applied',
    'already_applied',
    'world_mismatch',
    'biography_truncated',
];
/**
 * `details.reason` of a 409 `foundry_import_state` (CS r6b, DECISIONS 805.3; additive, so a later
 * stage appends): 2.I s2b's apply refusals (a `sheet` target until s3, a `link` the compendium does
 * not allow, a `variant` without a reference, a Foundry id another sheet holds, stored bytes that no
 * longer map), then the refresh and Undo reasons 2.I-2 s1's copy names (2.I s3 answers them).
 * Without a reason the answer's `details.status` alone says why.
 */
export const FOUNDRY_IMPORT_STATE_REASONS = [
    'sheet_target_unsupported',
    'link_not_unchanged',
    'no_reference',
    'foundry_id_taken',
    'not_mappable',
    'refresh_unsupported_kind',
    'not_newest',
    'changed_since',
];
/**
 * `details.issue` of a 422 `invalid_body` on `foundry.imports.apply` (a type or page that cannot hold
 * the sheet, a 2024 sheet) and `sheets.character.update` (a path no applied import holds, a path
 * both reverted and changed), with `details.path` (CS r6b, DECISIONS 805.3; additive); then a
 * refresh's choices on apply (2.I s3): a path the plan has no op for, a choice its op does not
 * offer (hotfix/demo-p2c, DECISIONS 811 ruling 10; additive).
 */
export const FOUNDRY_INVALID_BODY_ISSUES = [
    'unknown_type',
    'not_canon',
    'type_without_sheet',
    'document_trashed',
    'rules_2024',
    'not_revertible',
    'reverted_and_changed',
    'not_in_plan',
    'not_a_choice',
];
//# sourceMappingURL=rows-enums.js.map